<template>
	<!-- Une icône titrée porte seule son sens (« Compte bloqué »…) : elle est alors annoncée, pas
	     seulement survolable. Sans titre, elle reste décorative et masquée aux lecteurs d'écran. -->
	<img
		v-if="isMulticolor"
		:alt="icon as string"
		:src="getSrc()"
		:data-icon="icon"
		:title="title"
		:role="title ? 'img' : undefined"
		:aria-label="title"
		:aria-hidden="title ? undefined : 'true'"/>
	<span
		v-else
		:style="`--icon-src: url('${getSrc()}')`"
		:data-icon="icon"
		:title="title"
		:role="title ? 'img' : undefined"
		:aria-label="title"
		:aria-hidden="title ? undefined : 'true'"></span>
</template>

<script lang="ts" setup>
	import { computed } from "vue";
	import IconType from "@shared-types/icon.type";
	import ColorType from "@shared-types/color.type";
	import { iconDataUrls } from "@shared/icons-registry";

	const props = withDefaults(defineProps<{
		icon: IconType | undefined | null,
		title?: string,
		color?: ColorType,
		multicolor?: boolean,
	}>(), {
		multicolor: false,
	});

	// Drapeaux (`flag-*`) : intrinsèquement multicolores → rendus en <img> pour préserver leurs couleurs
	// (jamais en masque `currentColor`). Auto-détecté par préfixe → marche même via cfl-card. Les logos de
	// marque (`brands-*`) restent en masque par défaut (souvent monochromes/teintés) ; passer `multicolor`
	// explicitement pour les afficher en couleur.
	const isMulticolor = computed(() => props.multicolor || !! props.icon?.startsWith("flag-"));

	const getSrc = (): string =>
	{
		if (!props.icon) return '';

		const dataUrl = iconDataUrls.get(props.icon);

		if (!dataUrl && import.meta.env.DEV)
		{
			console.warn(`[cfl-icon] Icône inconnue : "${props.icon}"`);
		}

		return dataUrl ?? '';
	};
</script>

<style scoped>
	@reference "@main-css";

	span {
		@apply w-4 h-4 inline-block shrink-0;
		background-color: currentColor;
		-webkit-mask-image: var(--icon-src);
		mask-image: var(--icon-src);
		-webkit-mask-size: contain;
		mask-size: contain;
		-webkit-mask-repeat: no-repeat;
		mask-repeat: no-repeat;
		-webkit-mask-position: center;
		mask-position: center;
	}

	img {
		@apply w-4 h-4;
	}
</style>
